import educationClient from "./educationClient";
import { getEducationSession, invalidateEducationSession } from "./educationAuth";
import { withLookupCache, clearLookups } from "utils/lookupCache";
import { getApi, postApi, putApi, deleteApi } from "utils/apiCall";

// Thin wrapper around the Education/Toolbox backend, mirroring the shape of
// src/utils/apiCall.js (getApi/postApi -> { error, result }) and the sibling
// modules' own *Api.js files. Endpoint list cross-checked against the old
// client's own src/constants/ApiConstant.js (the authoritative map of which
// endpoint each screen actually calls) plus direct reads of the matching
// ToolboxAPI controllers for exact param sources.

const educationHeaders = async () => {
  const { tokenId, clientId } = await getEducationSession();
  return {
    "token-id": tokenId,
    "client-id": clientId,
    "device-type": process.env.REACT_APP_EDU_DEVICE_TYPE,
  };
};

// Same rotation-tolerant retry-on-401 pattern as the other three modules.
const withRetryOn401 = async (makeRequest) => {
  try {
    const headers = await educationHeaders();
    return await makeRequest(headers);
  } catch (error) {
    if (error?.response?.status !== 401) throw error;
    invalidateEducationSession();
    const headers = await educationHeaders();
    return await makeRequest(headers);
  }
};

const fetchGet = async (url) => {
  try {
    const response = await withRetryOn401((headers) => educationClient.get(url, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

// Dropdown/master lists, cached briefly (see utils/lookupCache).
const LOOKUPS = /\/(get_all_statelist|get_all_org_list_from_mym|get_all_fac_list_by_org_id_mym|get_sub_toolboox|get_all_memo_listing|get_all_competencies|get_toolbox_with_subeducation|get_mhra_with_subeducation|get_all_equipment_manual_types|education_toolboox_type|kpi_field_details)\b/;
export const getEduApi = (url) => withLookupCache("employee-connect", LOOKUPS, url, () => fetchGet(url));

export const postEduApi = async (url, data) => {
  clearLookups("employee-connect");
  try {
    const response = await withRetryOn401((headers) => educationClient.post(url, data, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

export const putEduApi = async (url, data) => {
  clearLookups("employee-connect");
  try {
    const response = await withRetryOn401((headers) => educationClient.put(url, data, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

export const deleteEduApi = async (url) => {
  clearLookups("employee-connect");
  try {
    const response = await withRetryOn401((headers) => educationClient.delete(url, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

// ---- Global / shared lookups ----
export const getOrgList = () => getEduApi("/get_all_org_list_from_mym");
export const getFacilitiesByOrgId = (orgId) => getEduApi(`/get_all_fac_list_by_org_id_mym/${orgId}`);
export const getAllStateList = () => getEduApi("/get_all_statelist");
// Opens a signed S3 URL for a stored PDF — confirmed by reading the old
// client's globalStore.getPreSignedUrl directly (every "View" action across
// this module goes through this, not a direct link on the row).
export const getPreSignedUrl = (url) => postEduApi("/get_presigned_url", { url });

// ---- Manage Education: Monthly Education tab (screen's own "Add Education" is
// a modal here, not a separate screen — confirmed by reading the old client
// directly) — MonthlyToolboxController.js ----
export const getEducationForFacilityManagement = (facilityId) => getEduApi(`/get_education_for_facility_management/${facilityId}`);
export const getMonthlyEducationCount = (facilityId) => getEduApi(`/get_monthly_education_count/${facilityId}`);
export const addMonthlyEducation = (body) => postEduApi("/insert_monthly_education", body);
export const getAllMonthlyEducationList = () => getEduApi("/get_all_monthly_education_list");
export const getOutstandingEducation = (facilityId) => getEduApi(`/get_outstanding_education/${facilityId}`);

// ---- Manage Education: Toolbox Education tab — EducationToolboxController.js ----
// get_toolboox_list's role_id path segment is optional per the route
// definition (":role_id?") — omit the trailing segment entirely when there's
// no role filter rather than passing an empty one.
export const getToolboxEducationTypes = (roleId) => getEduApi(`/education_toolboox_type?role_id=${roleId}`);
export const getToolboxTypesWithSubEducation = () => getEduApi("/get_toolbox_with_subeducation");
export const getSubEducationToolboxList = (id, roleId) => getEduApi(`/sub_education_toolboox_list/${id}${roleId ? `/${roleId}` : ""}`);
export const getToolboxEducationList = (id, subEducationTypeId, stateId, roleId) =>
  getEduApi(`/get_toolboox_list/${id}/${subEducationTypeId}/${stateId}${roleId ? `/${roleId}` : ""}`);
export const insertToolboxEducation = (body) => postEduApi("/insert_toolbox_education", body);
export const insertEducation = (body) => postEduApi("/insert_toolboox", body);
export const insertOtherToolbox = (body) => postEduApi("/insert_other_toolboox", body);
export const deleteToolboxEducation = (toolboxId) => getEduApi(`/delete_toolbox_education/${toolboxId}`); // real API deletes via GET, not DELETE
// Requires BOTH toolbox_id and title (req.query.title) or the real backend
// 400s — confirmed by reading getToolboxDetailsForWeb directly. The old
// app's own Select passes `value={(item?.title, item?.id)}`, a JS comma-
// operator bug that discards item.title and always sends title=undefined —
// so even the original screen's filter never actually works. Fixed here by
// passing the real title instead of reproducing that bug.
export const getToolboxDetailsForWeb = (toolboxId, title) =>
  getEduApi(`/get_toolbox_details_for_web?toolbox_id=${toolboxId}&title=${encodeURIComponent(title)}`);
export const getToolboxContent = (toolboxId) => getEduApi(`/get_toolbox_content/${toolboxId}`);
export const getRemainingStaffListByToolboxId = (toolboxId) => getEduApi(`/get_remaining_stafflist_by_toolbox_id/${toolboxId}`);
export const getSubToolbox = (id) => getEduApi(`/get_sub_toolboox/${id}`);
export const getToolboxByName = (body) => postEduApi("/get_toolbox_details_by_name", body);

// ---- Manage Education: Memo tab — MemoController.js ----
export const getMemoList = (stateId, roleId) => getEduApi(`/memo_list/${stateId}${roleId ? `/${roleId}` : ""}`);
export const getMemoById = (memoId, langCode) => getEduApi(`/memo_by_id_list/${memoId}/${langCode}`);
export const getAllMemoListing = () => getEduApi("/get_all_memo_listing");
export const getMemoContent = (memoId) => getEduApi(`/get_memo_content/${memoId}`);
export const insertMemo = (body) => postEduApi("/insert_memo_details", body);
export const deleteMemo = (memoId) => deleteEduApi(`/delete_memo_by_id/${memoId}`);
export const getMemoRegisterList = (body) => postEduApi("/get_memo_register_list", body); // body: {org_id, facility_id, state_id, memo_id}

// ---- Manage Education: SOP tab — SOPToolboxController.js ----
export const insertSop = (body) => postEduApi("/insert_sop_details", body);
export const getSopList = () => getEduApi("/get_sop_list");
export const deleteSop = (id) => deleteEduApi(`/delete_sop_details/${id}`);
export const getManualsTabs = () => getEduApi("/get_manuals_tabs");

// ---- Manage Education: MHRA tab — MHRAToolboxController.js ----
export const insertMhra = (body) => postEduApi("/insert_mhra_details", body);
export const getMhraList = () => getEduApi("/get_mhra_list");
export const deleteMhra = (id) => deleteEduApi(`/delete_mhra_details/${id}`);
export const getMhraWithSubEducation = () => getEduApi("/get_mhra_with_subeducation");

// ---- Manage Education / Registers: Competency tab — EducationCompetencyController.js ----
// create_competency = define a competency template; insert_competency = record
// one staff member's actual attendance/assessment against a toolbox — two
// distinct write operations, confirmed by reading the controller directly.
// client_id falls back to the client-id header (our own session's) when the
// query param is omitted — confirmed by reading get_competency_list directly.
// ---- Edit (EducationEditController.js): Monthly / Toolbox / SOP / MHRA / Memo
// content (toolbox_meeting rows) and Competency ----
export const getEducationContent = (id) => getEduApi(`/education_content/${id}`);
export const getMemoContentForEdit = (memoId) => getEduApi(`/education_content/memo/${memoId}`);
export const updateEducationContent = (id, body) => putEduApi(`/education_content/${id}`, body); // {title, topic, purpose?, state_id, for_staff, new_content, sub_toolbox_id?, education_date?}
export const getCompetencyForEdit = (toolboxMeetingId) => getEduApi(`/competency_edit/${toolboxMeetingId}`);
export const updateCompetency = (toolboxMeetingId, body) => putEduApi(`/competency/${toolboxMeetingId}`, body); // as createCompetency

export const getCompetencyList =(stateId) => getEduApi(`/get_competency_list${stateId ? `?state_id=${stateId}` : ""}`);
export const getCompetencyDetailsById = (id) => getEduApi(`/get_competency_details_by_id/${id}`);
export const getCompetency = (id, langCode) => getEduApi(`/get_competency/${id}/${langCode}`);
export const createCompetency = (body) => postEduApi("/create_competency", body);
export const insertCompetency = (body) => postEduApi("/insert_competency", body);
export const deleteCompetency = (id) => deleteEduApi(`/delete_competency/${id}`);
export const getAllCompetencies = () => getEduApi("/get_all_competencies");
export const getCompetencyRegisterList = (body) => postEduApi("/get_competency_register_list", body); // body: {org_id, facility_id, state_id, competency_id}

// ---- Reports tab ----
export const getAllEquipmentDetails = () => getEduApi("/all_equipment_details_for_mym");

// ---- Registers tab (facility-wise register listings, one endpoint per
// register type — all POST with the same {org_id, facility_id, state_id,
// <type>_id} body shape, confirmed by reading the register-list handlers
// directly) ----
export const getMonthlyEducationRegister = (body) => postEduApi("/get_monthly_education_register_list", body);
export const getToolboxEducationRegister = (body) => postEduApi("/get_toolbox_education_register_list", body);
export const getAllEquipmentData = (body) => postEduApi("/get_all_equipment_data", body); // body: {org_id, facility_id, state_id}

// ---- KPI tab — KpiController.js (all thin proxies to MyM, not local DB) ----
export const getKpiReport = (body) => postEduApi("/kpi_report", body); // body: {org_id, kpi_inspection_date}
export const addKpiReport = (body) => postEduApi("/add_kpi_report", body);
export const getKpiFieldDetailsByOrgId = (orgId) => getEduApi(`/kpi_field_details_by_orgid/${orgId}`);
export const getKpiFieldDetails = (facilityId) => getEduApi(`/kpi_field_details/${facilityId}`);
export const deleteKpiReport = (id) => deleteEduApi(`/kpi_report/${id}`);
export const getKpiReportByFacility = (body) => postEduApi("/get_kpi_report", body); // body: {facility_id}


// ---- Survey tab: generic survey module — SurveyTemplateController.js,
// SurveyFacilityAssignmentController.js, SurveySubmissionController.js ----
// Every response is { success, message, result }; a 200 with success:false is
// how these controllers report validation errors AND "no records" for lists.
// client_id is read from query, then body, then the client-id header.
const toSurveyQuery = (params = {}) => {
  const query = Object.keys(params)
    .filter((key) => params[key] !== undefined && params[key] !== null)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join("&");
  return query ? `?${query}` : "";
};
export const getSurveyTemplates = (params) => getEduApi(`/survey_templates${toSurveyQuery(params)}`); // params: {client_id, status?}
export const getSurveyTemplateById = (templateId) => getEduApi(`/survey_template/${templateId}`); // header + ordered questions
export const createSurveyTemplate = (body) => postEduApi("/survey_template", body); // header AND full question list in one call
// Header fields only (name/description/survey_type/is_active) — the backend
// ignores a `questions` array on this route; per-question edits go through
// the survey_template_question endpoints below.
export const updateSurveyTemplate = (templateId, body) => putEduApi(`/survey_template/${templateId}`, body);
export const deleteSurveyTemplate = (templateId) => deleteEduApi(`/survey_template/${templateId}`); // soft delete, cascades to questions
export const addSurveyTemplateQuestions = (templateId, body) => postEduApi(`/survey_template/${templateId}/questions`, body); // body: {questions: [...]}
// If the question already has answers the backend soft-deletes it and inserts
// a replacement, so historical submissions/PDFs keep their wording.
export const updateSurveyTemplateQuestion = (questionId, body) => putEduApi(`/survey_template_question/${questionId}`, body);
export const deleteSurveyTemplateQuestion = (questionId) => deleteEduApi(`/survey_template_question/${questionId}`);
export const assignSurveyTemplateToFacility = (body) => postEduApi("/survey_facility_assignment", body); // body: {survey_template_id, facility_id, client_id}
export const unassignSurveyTemplateFromFacility = (assignmentId) => deleteEduApi(`/survey_facility_assignment/${assignmentId}`); // soft unassign
export const getSurveyTemplatesByFacility = (facilityId, params) => getEduApi(`/survey_templates_by_facility/${facilityId}${toSurveyQuery(params)}`);
export const getSurveyFacilitiesByTemplate = (templateId) => getEduApi(`/survey_facilities_by_template/${templateId}`);
export const submitSurvey = (body) => postEduApi("/survey_submission", body); // body: {survey_template_id, facility_id, client_id, respondent_name, respondent_type, answers}
export const getSurveySubmissionHistory = (params) => getEduApi(`/survey_submission_history${toSurveyQuery(params)}`); // params: {facility_id?, survey_template_id?, month?, client_id}
export const getSurveySubmissionById = (submissionId) => getEduApi(`/survey_submission/${submissionId}`); // one submission with its answers (answer_display)
// Requires facility_id AND survey_template_id; month (YYYY-MM) defaults to
// the current month. Returns a single object, never a list.
export const getSurveyMonthlySummary = (params) => getEduApi(`/survey_monthly_summary${toSurveyQuery(params)}`);

// ---- KPI tab: KPI Setup — the ERP's own API (MyMApi KpiController.js): a
// group's KPI templates (name + frequency) and their rows, kept with the
// group's existing rows in m055_kpireport_fields ----
export const getKpiTemplates = (orgId) => getApi(`/kpi_templates/${orgId}`); // [{id, name, frequency, rows_count}]
export const getKpiTemplatesByFacility = (facilityId) => getApi(`/kpi_templates_by_facility/${facilityId}`); // [{kpi_template_id, name, frequency, current_period}]
export const getKpiTemplateFields = (templateId) => getApi(`/kpi_template_fields/${templateId}`);
export const addKpiTemplate = (body) => postApi("/kpi_template", body); // {org_id, name, frequency}
export const updateKpiTemplate = (id, body) => putApi(`/kpi_template/${id}`, body); // {name, frequency}
export const deleteKpiTemplate = (id) => deleteApi(`/kpi_template/${id}`);
export const addKpiField = (body) => postApi("/kpi_field", body); // {org_id, kpi_template_id?, measure_title, measure_data, target}
export const updateKpiField = (id, body) => putApi(`/kpi_field/${id}`, body); // same body
export const deleteKpiField = (id, orgId) => deleteApi(`/kpi_field/${id}?org_id=${orgId}`);

// ---- Manuals tab — EquipmentInstructionController.js ----
export const getManualList = (manualTypeId) => getEduApi(`/equipment_manual_list${manualTypeId ? `?equipment_manual_type_id=${manualTypeId}` : ""}`);
export const getSubManualList = (manualDetailsId) => getEduApi(`/sub_equipment_manual_list/${manualDetailsId}`);
export const getAllManualTypes = () => getEduApi("/get_all_equipment_manual_types");

// ---- Compliance Manual — ComplianceManualController.js: the client's manuals
// (name + PDF) shown in the app (GET /get_manuals_tabs) ----
export const getComplianceManuals = () => getEduApi("/compliance_manuals");
export const addComplianceManual = (body) => postEduApi("/compliance_manual", body); // {name, file (base64 PDF), filename}
export const updateComplianceManual = (id, body) => putEduApi(`/compliance_manual/${id}`, body); // {name?, file?, filename?}
export const deleteComplianceManual = (id) => deleteEduApi(`/compliance_manual/${id}`);
export const addManual = (body) => postEduApi("/add_equipment_manual", body);
export const updateManual = (manualDetailsId, body) => putEduApi(`/update_equipment_manual/${manualDetailsId}`, body);
export const deleteManual = (manualDetailsId) => deleteEduApi(`/delete_equipment_manual/${manualDetailsId}`);
export const addManualType = (body) => postEduApi("/add_equipment_manual_type", body);
export const deleteManualType = (manualTypeId) => deleteEduApi(`/delete_equipment_manual_type/${manualTypeId}`);
